import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from './contexts/AuthContext';
import {
  Box,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  Modal,
  Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import GameList from './components/GameList';
import { useNavigate } from 'react-router-dom';
import { useGameUtils } from './utilities/GameUtils';
import mapboxgl from 'mapbox-gl';
import CustomSwitch from './components/CustomSwitch';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useSearchUtils } from './utilities/SearchUtils';

// Set mapbox token
mapboxgl.accessToken =
  'pk.eyJ1IjoidW5pY29ybnJvYWQiLCJhIjoiY21iZG1pNzZqMDhzcTJsczJnMHZrZXk5ZCJ9.mkxEHjf22SfIQUotO9XtAw';

const Dashboard = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { getAllAvailableGameData } = useGameUtils();
  const { searchClubs } = useSearchUtils();
  const [availableGames, setAvailableGames] = useState([]);
  const [allGames, setAllGames] = useState([]); // Keep track of all games
  const [selectedGame, setSelectedGame] = useState(null);
  const [showMap, setShowMap] = useState(true);
  const [isGameCompetitive, setIsGameCompetitive] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showOverlay, setShowOverlay] = useState(false);
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const ENABLE_CHATBOT = false;

  useEffect(() => {
    if (showMap && mapContainerRef.current && !mapRef.current) {
      const map = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: 'mapbox://styles/mapbox/outdoors-v12',
        center: [-0.1276, 51.5072], // London default
        zoom: 1,
      });

      map.addControl(new mapboxgl.NavigationControl());
      mapRef.current = map;

      return () => {
        map.remove();
        mapRef.current = null;
      };
    }
  }, [showMap]);

  useEffect(() => {
    const _searchClubs = async () => {
      try {
        if (!searchQuery) {
          console.log('isGameCompetitive', allGames[0]);
          if (isGameCompetitive === 'competitive') {
            const competitiveGames = allGames.filter((game) => game.competitive === true);
            setAvailableGames(competitiveGames);
          } else if (isGameCompetitive === 'all') {
            setAvailableGames(allGames);
          } else if (isGameCompetitive === 'friendly') {
            const friendlyGames = allGames.filter((game) => game.competitive === false);
            setAvailableGames(friendlyGames);
          }
          return;
        }

        const clubs = await searchClubs(searchQuery, isGameCompetitive);
        const matchedClubIds = new Set(clubs.map((club) => club.clubId));
        const filteredGames = allGames.filter((game) => matchedClubIds.has(game.clubId));
        if (isGameCompetitive === 'competitive') {
          const competitiveGames = filteredGames.filter((game) => game.competitive === true);
          setAvailableGames(competitiveGames);
        } else if (isGameCompetitive === 'all') {
          setAvailableGames(filteredGames);
        } else if (isGameCompetitive === 'friendly') {
          const friendlyGames = filteredGames.filter((game) => game.competitive === false);
          setAvailableGames(friendlyGames);
        }
      } catch (error) {
        console.error('Error searching clubs:', error);
        setAvailableGames(allGames); // Reset to all games on error
      }
    };

    _searchClubs();
  }, [searchQuery, searchClubs, allGames, isGameCompetitive]);

  useEffect(() => {
    const fetchAvailableGames = async () => {
      const availableGameData = await getAllAvailableGameData();
      setAvailableGames(availableGameData.games);
      setAllGames(availableGameData.games);

      if (availableGameData.games?.length > 0 && mapRef.current) {
        const firstGame = availableGameData.games[0];
        mapRef.current.flyTo({
          center: [firstGame.clubLongitude, firstGame.clubLatitude],
          zoom: 4,
          duration: 1000,
        });
      }
    };
    fetchAvailableGames();
  }, [currentUser, getAllAvailableGameData]);

  useEffect(() => {
    if (!mapRef.current) return;

    // Clean up existing markers
    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];

    // Add markers
    availableGames.forEach((game) => {
      if (typeof game.clubLongitude === 'number' && typeof game.clubLatitude === 'number') {
        const el = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        el.setAttribute('width', '75');
        el.setAttribute('height', '75');
        el.setAttribute('viewBox', '0 0 25 25');
        el.setAttribute('fill', 'none');

        const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        rect.setAttribute('x', '0.0292969');
        rect.setAttribute('y', '0.957031');
        rect.setAttribute('width', '24');
        rect.setAttribute('height', '24');
        rect.setAttribute('rx', '12');
        rect.setAttribute('fill', '#8953EF');

        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute(
          'd',
          'M21.0293 7.35448L18.7908 15.3645C18.1009 17.7643 16.7823 18.9565 14.4365 18.9565C13.3172 18.9565 12.3445 18.6508 11.5012 18.1312C10.6273 18.6508 9.54547 18.9565 8.30362 18.9565C5.19115 18.9565 3.0293 16.9695 3.0293 13.8511C3.0293 12.2001 3.6579 10.4576 4.91519 8.47049L5.86573 6.95703L7.42961 7.85902L6.54042 9.23477C5.43646 10.9009 4.80786 12.3837 4.80786 13.7899C4.80786 15.9147 6.06506 17.3821 8.3189 17.3821C8.99357 17.3821 9.60004 17.2598 10.1213 17.0458C8.69541 15.5631 7.97488 14.4396 7.97488 12.8499C7.97488 10.4806 9.32401 8.89947 11.4092 8.89947C13.5097 8.89947 14.951 10.5096 14.951 12.8789C14.951 14.4228 14.2917 15.5783 12.8198 17.0916C13.2644 17.2752 13.7312 17.3821 14.2678 17.3821C15.5864 17.3821 16.4756 16.679 17.0123 14.7834L19.0821 7.35448H21.0293H21.0293ZM11.4245 10.4434C10.3666 10.4434 9.75345 11.3824 9.75345 12.8498C9.75345 14.2867 10.3206 15.0433 11.4552 16.1592C12.6512 15.0129 13.1878 14.224 13.1878 12.8789C13.1878 11.3962 12.4671 10.4434 11.4245 10.4434V10.4434Z'
        );
        path.setAttribute('fill', 'white');

        el.appendChild(rect);
        el.appendChild(path);
        el.style.cursor = 'pointer';

        const marker = new mapboxgl.Marker({
          element: el,
          anchor: 'center',
        })
          .setLngLat([game.clubLongitude, game.clubLatitude])
          .addTo(mapRef.current);

        marker.getElement().addEventListener('click', () => {
          mapRef.current.flyTo({
            center: [game.clubLongitude, game.clubLatitude],
            zoom: 13,
            duration: 1500,
            essential: true,
          });
          setSelectedGame(game);
          setShowOverlay(true);
        });

        markersRef.current.push(marker);
      }
    });
  }, [availableGames, showMap]);

  const handleMapToggle = (checked) => {
    setShowMap(checked);
  };


  const handleCloseOverlay = () => {
    setShowOverlay(false);
  };

  return (
    <Box sx={{ display: 'flex', maxWidth: '1060px', margin: '0 auto' }}>
      <Paper sx={{ flexGrow: 1, padding: 0 }} variant="rightPane">
        <Box sx={{ mb: 0 }}>
          {ENABLE_CHATBOT && (
            <>
              <Typography sx={{ my: 2 }}>
                Need help setting up your next round? Tell me what you need!
              </Typography>

              <TextField
                multiline
                fullWidth
                variant="outlined"
                id="ai-search"
                placeholder="Example: Set me up a game for friday with Jack at St Andrews"
              />
            </>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 24px',
            gap: '16px',
            width: '100%',
            borderBottom: '1px solid #E3DEEB',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: '16px',
              flex: 1,
            }}
          >
            <TextField
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Address, Club, City..."
              variant="outlined"
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '16px',
                  height: '66px',
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <Select
              value={isGameCompetitive}
              onChange={(e) => setIsGameCompetitive(e.target.value)}
              sx={{
                width: '192px',
                height: '66px',
                borderRadius: '16px',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '16px',
                },
              }}
            >
              <MenuItem value={'all'}>All</MenuItem>
              <MenuItem value={'competitive'}>Competitive</MenuItem>
              <MenuItem value={'friendly'}>Friendly</MenuItem>
            </Select>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '32px',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Plus Jakarta Sans',
                fontWeight: 500,
                fontSize: '16px',
                lineHeight: '20px',
                color: '#000000',
              }}
            >
              Show Map
            </Typography>
            <CustomSwitch checked={showMap} onChange={(e) => handleMapToggle(e.target.checked)} />
          </Box>
        </Box>

        {showMap && (
          <Box sx={{ position: 'relative' }}>
            <Box
              ref={mapContainerRef}
              sx={{
                width: '100%',
                height: '352px',
                position: 'relative',
                transition: 'height 0.3s ease-in-out',
              }}
            />
          </Box>
        )}

        {/* Game Information Overlay */}
        <Modal
          open={showOverlay}
          onClose={handleCloseOverlay}
          aria-labelledby="game-info-modal"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 24,
              p: 4,
              maxWidth: 500,
              maxHeight: '90vh',
              overflow: 'auto',
            }}
          >
            {selectedGame && (
              <>
                <Typography variant="h5" component="h2" gutterBottom>
                  {selectedGame.clubName}
                </Typography>
                <Typography variant="subtitle1" gutterBottom>
                  {selectedGame.courseName}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  Players: {selectedGame.players?.length ? selectedGame.players?.length : 0} of{' '}
                  {selectedGame.maxPlayers} spaces filled
                </Typography>
                {selectedGame.cost > 0 && (
                  <Typography variant="body1" gutterBottom>
                    Cost: £{selectedGame.cost}
                  </Typography>
                )}
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                  <Button variant="outlined" onClick={handleCloseOverlay}>
                    Close
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      handleCloseOverlay();
                      navigate(`/games/${selectedGame.id}`);
                      // Add any additional action here, like joining the game
                    }}
                  >
                    View Game
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Modal>

        <Box sx={{ pt: 3, px: 3 }}>
          <GameList 
            games={availableGames} 
            currentUser={currentUser} 
            cancelGame={() => {}} 
            useGrid={true} 
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default Dashboard;
